import React from "react";
import PropTypes from "prop-types";
import Carousel from "react-slick";
import withStyles from "@material-ui/core/styles/withStyles";

import { palette, typography, shape, shadows } from "../../theme/brand";

// Carousel of achievements. Each slide is split 50% image / 50% text, with the
// title rendered as white text overlapping the image. The image sits on a brand
// gradient backdrop (the source images are small, so it is contained rather than
// stretched). Replaces the template `NavPills` previously used for "Nuestros Logros".
// Relies on react-slick's base CSS, currently provided by the app's global
// stylesheet (`material-kit-pro-react.css`) — same as SponsorsCarouselSection.
function CarouselArrow({ classes, direction, onClick }) {
  const isNext = direction === "next";
  return (
    <button
      type="button"
      className={`${classes.arrow} ${
        isNext ? classes.arrowNext : classes.arrowPrev
      }`}
      onClick={onClick}
      aria-label={isNext ? "Siguiente logro" : "Logro anterior"}
    >
      {isNext ? "›" : "‹"}
    </button>
  );
}

CarouselArrow.propTypes = {
  classes: PropTypes.object.isRequired,
  direction: PropTypes.oneOf(["prev", "next"]).isRequired,
  onClick: PropTypes.func
};

const styles = {
  wrapper: {
    position: "relative",
    maxWidth: "960px",
    margin: "0 auto",
    "& .slick-dots": {
      bottom: "-38px"
    },
    "& .slick-dots li button:before": {
      fontSize: "11px",
      color: palette.primary,
      opacity: 0.35
    },
    "& .slick-dots li.slick-active button:before": {
      color: palette.primary,
      opacity: 1
    }
  },
  slide: {
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    borderRadius: shape.borderRadius,
    overflow: "hidden",
    backgroundColor: palette.surface,
    boxShadow: shadows.card,
    "@media (max-width: 768px)": {
      flexDirection: "column"
    }
  },
  imageHalf: {
    position: "relative",
    flex: "1 1 50%",
    minHeight: "320px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    background: `linear-gradient(135deg, ${palette.primary}, ${palette.accent})`,
    "@media (max-width: 768px)": {
      minHeight: "220px"
    }
  },
  image: {
    maxWidth: "85%",
    maxHeight: "240px",
    objectFit: "contain",
    borderRadius: "8px",
    boxShadow: "0 6px 18px rgba(0, 0, 0, 0.25)",
    transition: "transform 0.4s ease"
  },
  imageTitle: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    margin: 0,
    padding: "44px 20px 16px",
    color: "#fff",
    fontFamily: typography.fontFamilyDisplay,
    fontWeight: 700,
    fontSize: "1.15rem",
    lineHeight: 1.3,
    textShadow: "0 1px 4px rgba(0, 0, 0, 0.5)",
    background: "linear-gradient(to top, rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0))"
  },
  textHalf: {
    flex: "1 1 50%",
    display: "flex",
    alignItems: "center",
    padding: "32px 36px"
  },
  description: {
    color: palette.textSecondary,
    fontFamily: typography.fontFamilyBase,
    fontSize: "1rem",
    lineHeight: 1.7,
    margin: 0
  },
  arrow: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 2,
    width: "44px",
    height: "44px",
    borderRadius: "50%",
    border: "none",
    cursor: "pointer",
    backgroundColor: palette.primary,
    color: "#fff",
    fontSize: "26px",
    lineHeight: "44px",
    boxShadow: shadows.card,
    transition: "background-color 0.2s ease, transform 0.2s ease",
    "&:hover": {
      backgroundColor: palette.primaryDark,
      transform: "translateY(-50%) scale(1.08)"
    }
  },
  arrowPrev: {
    left: "8px"
  },
  arrowNext: {
    right: "8px"
  }
};

function PrizeCarousel({ classes, prizes }) {
  const settings = {
    dots: true,
    arrows: true,
    infinite: true,
    speed: 600,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 6000,
    pauseOnHover: true,
    adaptiveHeight: true,
    prevArrow: <CarouselArrow classes={classes} direction="prev" />,
    nextArrow: <CarouselArrow classes={classes} direction="next" />
  };

  return (
    <div className={classes.wrapper}>
      <Carousel {...settings}>
        {prizes.map(prize => (
          <div key={prize.title}>
            <div className={classes.slide}>
              <div className={classes.imageHalf}>
                <img
                  className={classes.image}
                  src={prize.image}
                  alt={prize.title}
                />
                <h3 className={classes.imageTitle}>{prize.title}</h3>
              </div>
              <div className={classes.textHalf}>
                <p className={classes.description}>{prize.description}</p>
              </div>
            </div>
          </div>
        ))}
      </Carousel>
    </div>
  );
}

PrizeCarousel.propTypes = {
  classes: PropTypes.object.isRequired,
  prizes: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired
    })
  ).isRequired
};

export default withStyles(styles)(PrizeCarousel);



// WEBPACK FOOTER //
// ./src/components/ui/PrizeCarousel.jsx